import React, { useRef, useEffect } from "react";
import { Bold, Italic, Underline, Heading, List, ListOrdered, Link2, MousePointerClick, Type, Eraser } from "lucide-react";

// Gmail-style no-code editor. Writes clean HTML to `value` in the background.
export const RichEmailEditor = ({ value, onChange, testid = "rich-email-editor" }) => {
  const ref = useRef(null);
  const last = useRef(value);

  useEffect(() => {
    if (ref.current && value !== last.current) {
      ref.current.innerHTML = value || "";
      last.current = value;
    }
  }, [value]);

  useEffect(() => {
    if (ref.current && !ref.current.innerHTML) ref.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    const h = ref.current?.innerHTML || "";
    last.current = h;
    onChange(h);
  };

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const insertHTML = (html) => {
    ref.current?.focus();
    document.execCommand("insertHTML", false, html);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link URL", "https://simo.university");
    if (url) exec("createLink", url);
  };

  const addCTA = () => {
    const label = window.prompt("Button label", "Apply now");
    if (!label) return;
    const url = window.prompt("Button link (URL)", "https://simo.university/apply");
    if (!url) return;
    insertHTML(
      `<p style="text-align:center;margin:24px 0"><a href="${url}" style="background:#5B3DF5;color:#ffffff;text-decoration:none;padding:12px 26px;border-radius:4px;font-family:Arial,sans-serif;font-size:14px;display:inline-block">${label}</a></p><p><br/></p>`
    );
  };

  const Btn = ({ onClick, title, children, tid }) => (
    <button
      type="button"
      title={title}
      data-testid={tid}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="text-paper/70 hover:text-white hover:bg-white/10 h-7 w-7 inline-flex items-center justify-center rounded-sm"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-white/10 rounded-sm overflow-hidden" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 bg-[#0B0D12] border-b border-white/10 px-2 py-1.5">
        <Btn onClick={() => exec("bold")} title="Bold" tid="editor-bold"><Bold className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => exec("italic")} title="Italic" tid="editor-italic"><Italic className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => exec("underline")} title="Underline" tid="editor-underline"><Underline className="h-3.5 w-3.5" /></Btn>
        <span className="w-px h-4 bg-white/10 mx-1" />
        <Btn onClick={() => exec("formatBlock", "H2")} title="Heading" tid="editor-heading"><Heading className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => exec("insertUnorderedList")} title="Bulleted list" tid="editor-ul"><List className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Numbered list" tid="editor-ol"><ListOrdered className="h-3.5 w-3.5" /></Btn>
        <span className="w-px h-4 bg-white/10 mx-1" />
        <Btn onClick={addLink} title="Insert link" tid="editor-link"><Link2 className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={addCTA} title="Insert button" tid="editor-cta"><MousePointerClick className="h-3.5 w-3.5" /></Btn>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => insertHTML("{{first_name}}")}
          data-testid="editor-firstname"
          className="text-[11px] text-paper/70 hover:text-white hover:bg-white/10 px-2 h-7 inline-flex items-center gap-1 rounded-sm"
          title="Insert recipient's first name"
        >
          <Type className="h-3 w-3" /> First name
        </button>
        <span className="w-px h-4 bg-white/10 mx-1" />
        <Btn onClick={() => exec("removeFormat")} title="Clear formatting" tid="editor-clear"><Eraser className="h-3.5 w-3.5" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid={`${testid}-area`}
        className="rich-email-body bg-white text-[#1a1a1a] px-4 py-3 text-sm leading-relaxed min-h-[240px] max-h-[460px] overflow-auto outline-none"
        style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
      />
    </div>
  );
};
